import { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { usePendingChanges } from '@/hooks/use-pending-changes';
import { PendingChangesFooter } from '@/components/pending-changes-footer';
import { NodeChangesModal, NodeChange } from '@/components/node-changes-modal';
import { NODE_TYPE_ORDER, NODE_TYPE_LABELS } from '@/constants/node-catalog';

export interface BaseNodeItem {
  node_id: string;
  display_name: string;
  description: string;
  node_type: string;
}

interface NodeCatalogListProps {
  items: BaseNodeItem[];
  getOriginalValue: (nodeId: string) => boolean;
  onSave: (changes: Record<string, boolean>) => Promise<void>;
  successMessage?: string;
  infoBanner?: React.ReactNode;
}

export const NodeCatalogList = ({
  items,
  getOriginalValue,
  onSave,
  successMessage = 'Node configuration saved successfully',
  infoBanner,
}: NodeCatalogListProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(NODE_TYPE_ORDER),
  );

  const {
    pendingChanges,
    setPendingChanges,
    showConfirmModal,
    setShowConfirmModal,
    isSaving,
    getCurrentValue,
    save,
    discard,
  } = usePendingChanges<boolean>({
    onSave,
    successMessage,
  });

  const handleToggle = useCallback(
    (nodeId: string, newValue: boolean) => {
      const originalValue = getOriginalValue(nodeId);
      setPendingChanges((prev) => {
        const newChanges = { ...prev };
        if (newValue === originalValue) {
          delete newChanges[nodeId];
        } else {
          newChanges[nodeId] = newValue;
        }
        return newChanges;
      });
    },
    [getOriginalValue, setPendingChanges],
  );

  const handleBulkToggle = useCallback(
    (enabled: boolean, targetItems?: BaseNodeItem[]) => {
      const bulkItems = targetItems || items;
      setPendingChanges((prev) => {
        const newChanges = { ...prev };
        for (const item of bulkItems) {
          const originalValue = getOriginalValue(item.node_id);
          if (enabled === originalValue) {
            delete newChanges[item.node_id];
          } else {
            newChanges[item.node_id] = enabled;
          }
        }
        return newChanges;
      });
    },
    [items, getOriginalValue, setPendingChanges],
  );

  const getDisplayValue = useCallback(
    (nodeId: string): boolean => {
      return getCurrentValue(nodeId, getOriginalValue(nodeId));
    },
    [getCurrentValue, getOriginalValue],
  );

  const getChangesForModal = useCallback((): NodeChange[] => {
    const changes: NodeChange[] = [];
    for (const [nodeId, newValue] of Object.entries(pendingChanges)) {
      const item = items.find((i) => i.node_id === nodeId);
      if (item) {
        changes.push({
          key: nodeId,
          label: item.display_name,
          oldValue: getOriginalValue(nodeId),
          newValue,
        });
      }
    }
    return changes;
  }, [pendingChanges, items, getOriginalValue]);

  const groupedCatalog = useMemo(() => {
    const filtered = searchQuery
      ? items.filter((item) =>
          item.display_name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : items;

    const groups: Record<string, BaseNodeItem[]> = {};
    for (const item of filtered) {
      const type = item.node_type || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(item);
    }

    for (const type in groups) {
      groups[type].sort((a, b) => a.display_name.localeCompare(b.display_name));
    }

    return groups;
  }, [items, searchQuery]);

  const getGroupStats = useCallback(
    (groupItems: BaseNodeItem[]) => {
      const enabled = groupItems.filter((item) =>
        getDisplayValue(item.node_id),
      ).length;
      return { enabled, total: groupItems.length };
    },
    [getDisplayValue],
  );

  const toggleGroup = useCallback((group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }, []);

  const sortedGroups = NODE_TYPE_ORDER.filter((type) => groupedCatalog[type]);

  return (
    <div className='space-y-4'>
      {infoBanner}

      <div className='flex items-center gap-3'>
        <div className='relative flex-1'>
          <Search className='absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            placeholder='Search nodes and tools...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className='pl-10'
          />
        </div>
        <Button
          variant='outline'
          size='sm'
          onClick={() => handleBulkToggle(true)}
        >
          Enable All
        </Button>
        <Button
          variant='outline'
          size='sm'
          onClick={() => handleBulkToggle(false)}
        >
          Disable All
        </Button>
      </div>

      {sortedGroups.map((nodeType) => {
        const groupItems = groupedCatalog[nodeType];
        const stats = getGroupStats(groupItems);
        const isExpanded = expandedGroups.has(nodeType);

        return (
          <Card key={nodeType}>
            <CardHeader
              className='cursor-pointer py-3'
              onClick={() => toggleGroup(nodeType)}
            >
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  {isExpanded ? (
                    <ChevronDown className='size-4' />
                  ) : (
                    <ChevronRight className='size-4' />
                  )}
                  <CardTitle className='text-base'>
                    {NODE_TYPE_LABELS[nodeType] || nodeType}
                  </CardTitle>
                  <span className='text-sm text-muted-foreground'>
                    ({stats.enabled}/{stats.total} enabled)
                  </span>
                </div>
                <div
                  className='flex items-center gap-2'
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className='text-xs text-muted-foreground'>
                    {stats.enabled === stats.total
                      ? 'All enabled'
                      : 'Enable all'}
                  </span>
                  <Switch
                    checked={stats.enabled === stats.total}
                    onCheckedChange={(checked) =>
                      handleBulkToggle(checked, groupItems)
                    }
                  />
                </div>
              </div>
            </CardHeader>
            {isExpanded && (
              <CardContent className='pt-0'>
                <div className='space-y-1'>
                  {groupItems.map((item, index) => {
                    const hasPendingChange = item.node_id in pendingChanges;
                    return (
                      <div key={item.node_id}>
                        {index > 0 && <Separator />}
                        <div className='flex items-center justify-between py-2'>
                          <div className='flex-1 pr-4'>
                            <div className='flex items-center gap-2'>
                              <span className='text-sm font-medium'>
                                {item.display_name}
                              </span>
                              {hasPendingChange && (
                                <span className='text-xs text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded'>
                                  Modified
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <div className='line-clamp-1 text-xs text-muted-foreground'>
                                {item.description}
                              </div>
                            )}
                          </div>
                          <Switch
                            checked={getDisplayValue(item.node_id)}
                            onCheckedChange={(checked) =>
                              handleToggle(item.node_id, checked)
                            }
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}

      {sortedGroups.length === 0 && (
        <div className='flex items-center justify-center py-12'>
          <p className='text-sm text-muted-foreground'>
            {searchQuery
              ? 'No nodes or tools match your search.'
              : 'No nodes or tools are available.'}
          </p>
        </div>
      )}

      <PendingChangesFooter
        changesCount={Object.keys(pendingChanges).length}
        onDiscard={discard}
        onReview={() => setShowConfirmModal(true)}
      />

      <NodeChangesModal
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        changes={getChangesForModal()}
        onSave={save}
        isSaving={isSaving}
      />
    </div>
  );
};
